import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { stanjeRobeAPI, stanjeRobeBrandsAPI } from '../services/api';
import { StanjeRobeBrand, StanjeRobeSupplier } from '../types';
import { showCustomAlert } from './CustomAlert';
import { useAuth } from '../contexts/AuthContext';

const resolveSupplierId = (supplierId: StanjeRobeBrand['supplierId']): string => {
  if (!supplierId) return '';
  return typeof supplierId === 'string' ? supplierId : supplierId._id;
};

const formatSupplierLabel = (s: StanjeRobeSupplier) => {
  const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${s.username})` : s.username;
};

const formatBrandSupplier = (brand: StanjeRobeBrand) => {
  const s = brand.supplierId;
  if (!s) return '—';
  if (typeof s === 'string') return s;
  return formatSupplierLabel(s as StanjeRobeSupplier);
};

const StanjeRobeBrands: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'direktor';

  const [brands, setBrands] = useState<StanjeRobeBrand[]>([]);
  const [suppliers, setSuppliers] = useState<StanjeRobeSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<StanjeRobeBrand | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', description: '', supplierId: '' });

  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ label: formatSupplierLabel(s), value: s._id })),
    [suppliers]
  );

  const load = async () => {
    setLoading(true);
    try {
      setBrands(await stanjeRobeBrandsAPI.getAll());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri učitavanju brendova');
    } finally {
      setLoading(false);
    }
  };

  const loadSuppliers = async () => {
    if (!isAdmin) return;
    try {
      setSuppliers(await stanjeRobeAPI.getSuppliers());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri učitavanju supplier-a');
    }
  };

  useEffect(() => {
    void load();
    void loadSuppliers();
  }, [isAdmin]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', supplierId: suppliers.length === 1 ? suppliers[0]._id : '' });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (b: StanjeRobeBrand) => {
    setEditing(b);
    setForm({
      name: b.name,
      description: b.description || '',
      supplierId: resolveSupplierId(b.supplierId)
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Naziv je obavezan'); return; }
    if (isAdmin && !form.supplierId) { setError('Izaberite supplier-a'); return; }
    try {
      const payload = {
        name: form.name,
        description: form.description,
        ...(isAdmin ? { supplierId: form.supplierId } : {})
      };
      if (editing) await stanjeRobeBrandsAPI.update(editing._id, payload);
      else await stanjeRobeBrandsAPI.create(payload);
      setDialogVisible(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri čuvanju');
    }
  };

  const handleDelete = (b: StanjeRobeBrand) => {
    showCustomAlert.confirm(`Obrisati brend "${b.name}"?`, async () => {
      try {
        await stanjeRobeBrandsAPI.delete(b._id);
        await load();
      } catch (err: any) {
        showCustomAlert.error(err?.response?.data?.message || 'Greška pri brisanju');
      }
    });
  };

  return (
    <div className="p-3 md:p-4">
      <Card title="Brendovi robe">
        {error && <Message severity="error" text={error} className="mb-3" />}
        {isAdmin && suppliers.length === 0 && (
          <Message
            severity="warn"
            text="Nema kreiranih supplier korisnika. Prvo kreirajte korisnika sa ulogom Supplier u Podešavanjima."
            className="mb-3"
          />
        )}
        <div className="flex justify-content-end mb-3">
          <Button label="Dodaj brend" icon="pi pi-plus" onClick={openCreate} disabled={isAdmin && suppliers.length === 0} />
        </div>
        <DataTable value={brands} loading={loading} paginator rows={15} emptyMessage="Nema brendova.">
          <Column field="name" header="Naziv" sortable />
          {isAdmin && <Column header="Supplier" body={formatBrandSupplier} sortable />}
          <Column field="description" header="Opis" />
          <Column
            header="Akcije"
            body={(row: StanjeRobeBrand) => (
              <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text onClick={() => openEdit(row)} />
                <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => handleDelete(row)} />
              </div>
            )}
            style={{ width: 120 }}
          />
        </DataTable>
      </Card>

      <Dialog header={editing ? 'Izmeni brend' : 'Novi brend'} visible={dialogVisible} onHide={() => setDialogVisible(false)} style={{ width: '500px' }}>
        <div className="p-fluid">
          {isAdmin && (
            <div className="field">
              <label>Supplier *</label>
              <Dropdown
                value={form.supplierId}
                options={supplierOptions}
                onChange={(e) => setForm({ ...form, supplierId: e.value })}
                placeholder="Izaberite supplier-a"
                filter
                appendTo={document.body}
              />
              <small className="text-color-secondary">
                Podaci vezani za brend videće samo izabrani supplier.
              </small>
            </div>
          )}
          <div className="field">
            <label>Naziv *</label>
            <InputText value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="field">
            <label>Opis</label>
            <InputTextarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} autoResize />
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Otkaži" outlined severity="secondary" onClick={() => setDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default StanjeRobeBrands;
